import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wrench, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function SignupPage() {
  const [form, setForm] = useState({ name: "", email: "", password: "", location_id: "" });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/locations")
      .then((r) => {
        if (!cancelled) setLocations(r.data);
      })
      .catch((err) => {
        // Locations are optional on signup; log for diagnostics, fall back to empty list.
        console.warn("Failed to load locations for signup:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.location_id) return toast.error("Please choose your location");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.post("/auth/signup", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        location_id: form.location_id,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFD]">
        <div className="flex items-center justify-center px-6 py-12 lg:px-16">
          <div className="w-full max-w-sm animate-fade-up text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6">
              <CheckCircle2 size={26} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">
              Almost there
            </h1>
            <p className="text-sm text-gray-600 mb-8">
              Your account has been created. An admin will review and approve it shortly. You'll be able to log in once approved.
            </p>
            <Button
              data-testid="back-to-login"
              onClick={() => navigate("/login", { replace: true })}
              className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC]"
            >
              Back to sign in
            </Button>
          </div>
        </div>
        <div
          className="hidden lg:block"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%), url('https://images.unsplash.com/photo-1754666095009-5b81f7435a84?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFD]">
      <div className="flex items-center justify-center px-6 py-12 lg:px-16">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-[#0055FF] text-white flex items-center justify-center shadow-sm">
              <Wrench size={18} strokeWidth={2.4} />
            </div>
            <div>
              <p className="font-display font-bold text-xl leading-none tracking-tight">FixBoard</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">Maintenance OS</p>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">
            Sign up
          </h1>
          <p className="text-sm text-gray-600 mb-8">
            Request access for your location. An admin will approve your account before you can sign in.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-gray-700">Full name</Label>
              <Input
                data-testid="signup-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Jane Smith"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-gray-700">Email</Label>
              <Input
                data-testid="signup-email"
                type="email"
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-gray-700">Password</Label>
              <Input
                data-testid="signup-password"
                type="password"
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 6 characters"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-gray-700">Your location</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                <SelectTrigger data-testid="signup-location" className="h-11">
                  <SelectValue placeholder="Choose your store…" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locations.length === 0 && (
                <p className="text-xs text-gray-500">No locations available yet — ask your admin.</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white font-medium"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  Create account <ArrowRight size={16} className="ml-1" />
                </>
              )}
            </Button>

            <p className="text-sm text-gray-600 text-center">
              Already have an account?{" "}
              <Link to="/login" data-testid="link-to-login" className="text-[#0055FF] font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>

      <div
        className="hidden lg:block relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%), url('https://images.unsplash.com/photo-1754666095009-5b81f7435a84?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute bottom-0 left-0 right-0 p-10 text-white">
          <p className="text-xs tracking-[0.2em] uppercase opacity-80 mb-3">Field-ready</p>
          <h2 className="font-display text-3xl font-bold tracking-tight leading-tight max-w-sm">
            Report problems from your location in seconds.
          </h2>
        </div>
      </div>
    </div>
  );
}
